import React, { useMemo, useRef, useState } from 'react';
import {
  Container,
  Title,
  Text,
  Button,
  Group,
  Stack,
  Card,
  Badge,
  FileInput,
  Textarea,
  Alert,
  Loader,
  Divider,
  Select,
  NumberInput,
  Switch,
  Table,
  ActionIcon,
  Tooltip
} from '@mantine/core';
import {
  IconArrowLeft,
  IconUpload,
  IconAlertCircle,
  IconTrash,
  IconBook2
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { importKb, judgeImport } from '../api_scripts/importsClient';
import { isSitBackendConfigured } from '../api_scripts/sitApiClient';
import SynthesizedPlanList from './SynthesizedPlanList';
import JudgeProgressModal from '../../../components/JudgeProgressModal';

// Backend caps: 5 artifacts × 32MB PDF / 200K-char text. Frontend warns earlier.
const MAX_ARTIFACTS = 5;
const PDF_SOFT_WARN_BYTES = 28 * 1024 * 1024;
const TEXT_SOFT_WARN_CHARS = 180_000;
const ACCEPTED_TYPES = '.pdf,.md,.markdown,.html,.htm,.txt';

const DEFAULT_MAX_PLANS = 12;

const CCAAS_OPTIONS = [
  { value: 'wxcc', label: 'WebEx Contact Center' },
  { value: 'aws-connect', label: 'AWS Connect' },
  { value: 'five9', label: 'Five9' },
  { value: 'genesys', label: 'Genesys' }
];

// AI-agent prompts run 5–12s typical, 15s default gives Sonnet a beat.
const IVR_PACE_PRESETS = { fast: 5, standard: 8, slow: 15 };
const IVR_PACE_OPTIONS = [
  { value: 'fast', label: 'Fast — touch-tone-feel (5s)' },
  { value: 'standard', label: 'Standard — modern AI agent (8s)' },
  { value: 'slow', label: 'Slow — long-think AI / IVA (15s)' },
  { value: 'custom', label: 'Custom — set my own…' }
];

function resolveBaseTimeout(pace, custom) {
  if (pace === 'custom') {
    const n = Number(custom);
    if (!Number.isFinite(n)) return IVR_PACE_PRESETS.slow;
    return Math.min(30, Math.max(5, Math.round(n)));
  }
  return IVR_PACE_PRESETS[pace] ?? IVR_PACE_PRESETS.slow;
}

function newArtifactRow() {
  return {
    id: `kb${Math.random().toString(36).slice(2, 8)}`,
    label: '',
    file: null
  };
}

function KbImporter({ onBack }) {
  const [rows, setRows] = useState([newArtifactRow()]);
  const [inlineText, setInlineText] = useState('');
  const [notes, setNotes] = useState('');
  const [maxPlans, setMaxPlans] = useState(DEFAULT_MAX_PLANS);

  // Test-mix knobs.
  const [positivePerTopic, setPositivePerTopic] = useState(1);
  const [includeNegative, setIncludeNegative] = useState(true);
  const [includePersona, setIncludePersona] = useState(true);

  // Target defaults — applied to every generated plan's target block. The
  // frontend overwrites destination/fromDid at run time, but the model needs
  // a placeholder shape to satisfy the schema.
  const [ccaas, setCcaas] = useState('wxcc');

  // Pacing.
  const [ivrPace, setIvrPace] = useState('standard');
  const [customTimeoutSec, setCustomTimeoutSec] = useState(15);

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [selectedIndexes, setSelectedIndexes] = useState(new Set());

  // Quality-judge state. `judgeRunIdRef` lets us discard stale judge
  // responses when the user regenerates while a previous judge is still
  // in flight — see startJudge below.
  const [judgeEvaluating, setJudgeEvaluating] = useState(false);
  const [qualityScore, setQualityScore] = useState(null);
  const [qualityFindings, setQualityFindings] = useState([]);
  const judgeRunIdRef = useRef(0);
  // Last submitted payload — regenerate refires this with priorJudgeFindings.
  const lastPayloadRef = useRef(null);

  const navigate = useNavigate();
  const backendConfigured = isSitBackendConfigured();

  const filled = useMemo(() => rows.filter((r) => r.file), [rows]);
  const inlineNonEmpty = inlineText.trim().length > 0;
  const canSubmit = !submitting && (filled.length > 0 || inlineNonEmpty);

  const updateRow = (idx, patch) => {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  const handleFileChange = (idx, file) => {
    setError(null);
    if (!file) {
      updateRow(idx, { file: null });
      return;
    }
    // Soft-warn on PDF size.
    if ((file.type || '').includes('pdf') && file.size > PDF_SOFT_WARN_BYTES) {
      setError(
        `${file.name}: ${(file.size / 1024 / 1024).toFixed(1)} MB is near the 32 MB Bedrock PDF cap. Submit may fail.`
      );
    }
    updateRow(idx, {
      file,
      label: rows[idx]?.label || file.name.replace(/\.[^.]+$/, '').slice(0, 60)
    });
  };

  const addRow = () => {
    setRows((prev) => (prev.length >= MAX_ARTIFACTS ? prev : [...prev, newArtifactRow()]));
  };

  const removeRow = (idx) => {
    setRows((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));
  };

  // Build a plain-text snapshot of the KB the judge will grade against.
  // PDFs become "[pdf: <label>]" stubs (the model can't ground against bytes
  // it didn't see); markdown/HTML/TXT we already read inline and can include.
  const buildKbSourceContent = async (artifacts, inlineBody) => {
    const parts = [];
    for (const a of artifacts) {
      if (!a.file) continue;
      const isPdf = (a.file.type || '').includes('pdf');
      if (isPdf) {
        parts.push(`[pdf artifact "${a.label || a.id}" — ${(a.file.size / 1024).toFixed(0)} KB; content not re-read by the judge]`);
      } else {
        try {
          const text = await a.file.text();
          parts.push(`## ${a.label || a.id}\n\n${text}`);
        } catch {
          parts.push(`[failed to re-read artifact "${a.label || a.id}"]`);
        }
      }
    }
    if (inlineBody) parts.push(`## (engineer-pasted KB)\n\n${inlineBody}`);
    return parts.join('\n\n---\n\n');
  };

  const startJudge = async ({ plans, artifacts, inlineBody }) => {
    const runId = ++judgeRunIdRef.current;
    setJudgeEvaluating(true);
    setQualityScore(null);
    setQualityFindings([]);
    try {
      const sourceContent = await buildKbSourceContent(artifacts, inlineBody);
      const data = await judgeImport({
        kind: 'sit-plan',
        source: {
          kind: 'kb',
          description: `${artifacts.length} artifact(s) + ${
            inlineBody ? `${inlineBody.length} chars inline` : 'no inline'
          }`
        },
        sourceContent,
        plans
      });
      if (judgeRunIdRef.current !== runId) return; // stale
      if (typeof data?.overallScore === 'number') setQualityScore(data.overallScore);
      if (Array.isArray(data?.perItem)) setQualityFindings(data.perItem);
    } catch (e) {
      if (judgeRunIdRef.current !== runId) return;
      // Judge is advisory — never block the result panel on a judge failure.
      // Log so the engineer can see it in the console; UI just drops the badge.
      // eslint-disable-next-line no-console
      console.warn('quality-judge call failed:', e);
    } finally {
      if (judgeRunIdRef.current === runId) setJudgeEvaluating(false);
    }
  };

  const runImport = async (priorJudgeFindings) => {
    setError(null);
    setResult(null);
    setSubmitting(true);
    setQualityScore(null);
    setQualityFindings([]);
    try {
      const artifacts = filled.map((r) => ({
        id: r.id,
        label: r.label || r.file.name.replace(/\.[^.]+$/, '').slice(0, 60),
        file: r.file
      }));
      const payload = {
        artifacts,
        inlineText: inlineNonEmpty ? inlineText : undefined,
        notes: notes.trim() || undefined,
        maxPlans,
        testMix: {
          positivePerTopic,
          includeNegative,
          includePersona
        },
        targetDefaults: { ccaas },
        expectPromptBaseTimeoutSec: resolveBaseTimeout(ivrPace, customTimeoutSec),
        ...(priorJudgeFindings && priorJudgeFindings.length > 0
          ? { priorJudgeFindings }
          : {})
      };
      lastPayloadRef.current = { artifacts, inlineBody: inlineNonEmpty ? inlineText : '' };
      const data = await importKb(payload);
      setResult(data);
      setSelectedIndexes(new Set((data.plans || []).map((_, i) => i)));
      if (data.plans?.length > 0) {
        startJudge({ plans: data.plans, ...lastPayloadRef.current });
      }
    } catch (e) {
      const body = e?.response?.data;
      setError(
        body?.context?.detail ||
          body?.message ||
          body?.code ||
          e.message ||
          'KB import failed.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    return runImport();
  };

  const handleRegenerate = async (findings) => runImport(findings);

  return (
    <Container size="xl">
      <Group justify="space-between" mb="md">
        <Group gap="sm">
          <IconBook2 size={28} />
          <Title order={2}>Conversational AI-Agent Tests (from KB)</Title>
        </Group>
        <Button variant="subtle" leftSection={<IconArrowLeft size={14} />} onClick={onBack}>
          Back to SIT
        </Button>
      </Group>

      <Text c="dimmed" mb="md">
        Upload a customer knowledge base (PDF, Markdown, HTML, or plain text). Bedrock Sonnet 4.6
        synthesizes a set of conversational test plans that exercise an autonomous AI agent grounded
        in that KB. Positive tests check KB-grounded answers, negative tests verify the agent
        escalates on off-KB questions, persona tests verify the agent stays in character.
      </Text>

      {!backendConfigured && (
        <Alert color="yellow" icon={<IconAlertCircle size={16} />} mb="md">
          <code>REACT_APP_SIT_API_BASE</code> is not set; submission will fail.
        </Alert>
      )}

      <Card withBorder shadow="sm" radius="md" p="lg" mb="md">
        <Stack gap="sm">
          <Title order={5}>KB artifacts</Title>
          <Text size="sm" c="dimmed">
            Up to 5 files. PDFs ride Bedrock's document content block (32 MB / 100 pages each).
            Markdown / HTML / TXT pass inline. Or paste KB content directly in the textarea below.
          </Text>

          <Table withTableBorder withColumnBorders verticalSpacing="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: '35%' }}>Label</Table.Th>
                <Table.Th>File</Table.Th>
                <Table.Th style={{ width: 60 }}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((r, i) => (
                <Table.Tr key={r.id}>
                  <Table.Td>
                    <Textarea
                      autosize
                      minRows={1}
                      placeholder={`KB article ${i + 1}`}
                      value={r.label}
                      onChange={(e) => updateRow(i, { label: e.currentTarget.value })}
                    />
                  </Table.Td>
                  <Table.Td>
                    <FileInput
                      placeholder="Choose .pdf / .md / .html / .txt"
                      accept={ACCEPTED_TYPES}
                      value={r.file}
                      leftSection={<IconUpload size={14} />}
                      onChange={(file) => handleFileChange(i, file)}
                      clearable
                    />
                  </Table.Td>
                  <Table.Td>
                    {rows.length > 1 && (
                      <Tooltip label="Remove this row">
                        <ActionIcon variant="subtle" color="red" onClick={() => removeRow(i)}>
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>

          <Group justify="flex-start">
            <Button variant="light" size="xs" onClick={addRow} disabled={rows.length >= MAX_ARTIFACTS}>
              + Add another artifact
            </Button>
          </Group>

          <Divider label="OR / additionally — paste KB content" labelPosition="left" />

          <Textarea
            autosize
            minRows={3}
            maxRows={12}
            placeholder="Paste KB markdown, FAQ entries, prompt-and-answer pairs, etc."
            value={inlineText}
            onChange={(e) => setInlineText(e.currentTarget.value)}
            description={
              inlineText.length > TEXT_SOFT_WARN_CHARS
                ? `${inlineText.length.toLocaleString()} chars — near the 200K backend cap.`
                : null
            }
          />
        </Stack>
      </Card>

      <Card withBorder shadow="sm" radius="md" p="lg" mb="md">
        <Stack gap="sm">
          <Title order={5}>Test mix</Title>
          <Group grow>
            <NumberInput
              label="Positive tests per topic"
              description="Caller asks the canonical KB question; agent should answer with KB facts."
              min={0}
              max={5}
              value={positivePerTopic}
              onChange={(v) => setPositivePerTopic(Number(v) || 0)}
            />
            <NumberInput
              label="Total plans (cap)"
              description="Backend ceiling is 25."
              min={1}
              max={25}
              value={maxPlans}
              onChange={(v) => setMaxPlans(Number(v) || DEFAULT_MAX_PLANS)}
            />
          </Group>
          <Group>
            <Switch
              label="Include negative tests (off-KB → escalate)"
              checked={includeNegative}
              onChange={(e) => setIncludeNegative(e.currentTarget.checked)}
            />
            <Switch
              label="Include persona tests (rude / confused / repeat)"
              checked={includePersona}
              onChange={(e) => setIncludePersona(e.currentTarget.checked)}
            />
          </Group>
        </Stack>
      </Card>

      <Card withBorder shadow="sm" radius="md" p="lg" mb="md">
        <Stack gap="sm">
          <Title order={5}>Pacing &amp; target defaults</Title>
          <Group grow>
            <Select
              label="Agent pacing"
              data={IVR_PACE_OPTIONS}
              value={ivrPace}
              onChange={(v) => setIvrPace(v || 'standard')}
              description="Default expectPrompt timeoutSec. AI agents typically need 8–15s."
            />
            {ivrPace === 'custom' && (
              <NumberInput
                label="Custom timeoutSec"
                min={5}
                max={30}
                value={customTimeoutSec}
                onChange={(v) => setCustomTimeoutSec(Number(v) || 15)}
              />
            )}
            <Select
              label="Target CCaaS"
              data={CCAAS_OPTIONS}
              value={ccaas}
              onChange={(v) => setCcaas(v || 'wxcc')}
            />
          </Group>
          <Textarea
            label="Engineer notes (optional)"
            autosize
            minRows={2}
            placeholder="Extra context for the model — deployment notes, persona expectations, channel hints, etc."
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
          />
        </Stack>
      </Card>

      <Group justify="flex-end" mb="md">
        <Button
          size="md"
          onClick={handleSubmit}
          disabled={!canSubmit}
          leftSection={submitting ? <Loader size={14} color="white" /> : null}
        >
          {submitting ? 'Synthesizing test plans…' : 'Generate plans'}
        </Button>
      </Group>

      {error && (
        <Alert color="red" icon={<IconAlertCircle size={16} />} mb="md">
          {error}
        </Alert>
      )}

      {result?.plans?.length > 0 && (
        <SynthesizedPlanList
          plans={result.plans}
          modelId={result.modelId}
          retries={result.retries}
          warnings={result.warnings || []}
          sourceLabel="KB import"
          selectable
          selectedIndexes={selectedIndexes}
          onToggleSelect={(i) => {
            setSelectedIndexes((prev) => {
              const next = new Set(prev);
              if (next.has(i)) next.delete(i);
              else next.add(i);
              return next;
            });
          }}
          extraHeaderActions={
            <Badge variant="dot" color="violet">
              {result.kbArtifacts?.length ?? 0} KB files persisted
            </Badge>
          }
          onVerify={(plans) =>
            navigate('/verify', { state: { plans, source: 'KB import' } })
          }
          evaluating={judgeEvaluating}
          qualityScore={qualityScore}
          qualityFindings={qualityFindings}
          onRegenerate={handleRegenerate}
        />
      )}

      <JudgeProgressModal
        opened={judgeEvaluating}
        onClose={() => { /* parent controls via evaluating flag */ }}
        kind="sit-plan"
        color="violet"
      />
    </Container>
  );
}

export default KbImporter;
